import {
  ReportAggregationType,
  ReportFilterOperation,
  ReportMetricType,
  ReportQuery,
} from '@driftt/api-types'
import {
  ReportAggregationSortType,
  ReportOrderType,
} from '@driftt/api-types/dist/report'

import {
  withCSATFilters,
  withMissedChatFilters,
} from 'api/report/realTime/helpers'

import AggregationBuilder from '../query/AggregationBuilder'
import { IQueryOptions } from '../query/types'

import * as Parsers from './parsers'
import QueryBuilder from './RealTimeQueryBuilder'
import { IRealTimeQueryConfig, ParserFunction, RTDReportTypes } from './types'

const makeQueries = ({
  sortConfig,
  botId,
  metric,
}: IRealTimeQueryConfig & IQueryOptions): {
  [key: string]: ReportQuery
} => {
  // autocsat is sometimes using no agent id
  const botValues: any[] = [0]

  if (botId) {
    botValues.push(botId)
  }
  return {
    [RTDReportTypes.LOW_CSAT]: withCSATFilters(
      new QueryBuilder('latestLiveChatRating.ratedAt')
        .withAggregation(
          new AggregationBuilder(
            'latestLiveChatRating.ratingValue',
            ReportAggregationType.HISTOGRAM
          )
            .withSize(20)
            .withMetric(
              'latestLiveChatRating.ratingValue',
              ReportMetricType.TOP_HITS
            )
            .withSort({ metric: 'lastRated', order: ReportOrderType.DESC })
            .build()
        )
        .withQueryName(metric),
      botValues
    ),
    [RTDReportTypes.LOW_CSAT_SEARCH]: withCSATFilters(
      new QueryBuilder('latestLiveChatRating.ratedAt').withSize(100),
      botValues
    ).withQueryName(metric),
    [RTDReportTypes.NOT_RESPONDED]: new QueryBuilder()
      .withAggregation(
        new AggregationBuilder(
          'firstRoutingAction.participants',
          ReportAggregationType.TERMS
        )
          .withDocCount()
          .build()
      )
      .withQueryName(metric)
      .withFilter(
        'firstRoutingAction.participants',
        ReportFilterOperation.HAS_PROPERTY
      )
      .withFilter('firstAgentMessageAt', ReportFilterOperation.NOT_HAS_PROPERTY)
      .withFilter('status', ReportFilterOperation.IN, ['open']),
    [RTDReportTypes.REP_PERFORMANCE]: new QueryBuilder()
      .withAggregation(
        new AggregationBuilder('firstAgentId', ReportAggregationType.TERMS)
          .withDocCount()
          .withMetric(
            'timeToAgentResponseAfterBotMillis',
            ReportMetricType.MEDIAN
          )
          .withMetric('timeToCloseMillis', ReportMetricType.MEDIAN)
          .withMetric('agentAverageResponseMillis', ReportMetricType.AVG)
          .withSort(sortConfig)
          .build()
      )
      .withFilter(
        'timeToAgentResponseAfterBotMillis',
        ReportFilterOperation.HAS_PROPERTY
      )
      .withQueryName(metric),
    [RTDReportTypes.CHAT_RATINGS]: new QueryBuilder()
      .withAggregation(
        new AggregationBuilder(
          'latestLiveChatRating.attributedRatingAgentId',
          ReportAggregationType.TERMS
        )
          .withDocCount()
          .withMetric('latestLiveChatRating.ratingValue', ReportMetricType.AVG)
          .withSize(0)
          .build()
      )
      .withQueryName(metric),
    [RTDReportTypes.OPEN_CLOSED_CONVOS]: new QueryBuilder()
      .withAggregation(
        new AggregationBuilder('firstAgentId', ReportAggregationType.TERMS)
          .withDocCount()
          .withAggregation(
            new AggregationBuilder(
              'status.keyword',
              ReportAggregationType.TERMS
            )
              .withDocCount()
              .build()
          )
          .build()
      )
      .withFilter('status', ReportFilterOperation.IN, ['closed', 'open'])
      .withQueryName(metric),
    [RTDReportTypes.MISSED_CHATS_TYPE]: withMissedChatFilters(
      new QueryBuilder().withAggregation(
        new AggregationBuilder(
          'routingFallbackSummary.actionType',
          ReportAggregationType.TERMS
        )
          .withMetric(
            'routingFallbackSummary.actionType',
            ReportMetricType.TOP_HITS,
            undefined,
            {
              minDocCount: 40,
              sort: {
                property: 'routingFallbackSummary.fallbackActionAt',
                type: ReportAggregationSortType.METRIC,
                metricType: ReportMetricType.MAX,
                order: ReportOrderType.DESC,
              },
            }
          )
          .withMissingValue('OFFLINE')
          .build()
      )
    ).withQueryName(metric),
    [RTDReportTypes.MISSED_CHATS_SEARCH]: withMissedChatFilters(
      new QueryBuilder().withSize(1000).withQueryName(metric)
    ),
    [RTDReportTypes.MISSED_CHATS_AGENT]: withMissedChatFilters(
      new QueryBuilder().withAggregation(
        new AggregationBuilder(
          'firstRoutingAction.participants',
          ReportAggregationType.TERMS
        )
          .withDocCount()
          .withAggregation(
            new AggregationBuilder(
              'routingFallbackSummary.actionType',
              ReportAggregationType.TERMS
            )
              .withDocCount()
              .withMissingValue('UNAVAILABLE')
              .build()
          )
          .build()
      )
    ).withQueryName(metric),
    [RTDReportTypes.LEADS]: new QueryBuilder('latestCQLData.updatedAt')
      .withAggregation(
        new AggregationBuilder(
          'latestCQLData.authorId',
          ReportAggregationType.TERMS
        )
          .withDocCount()
          .withAggregation(
            new AggregationBuilder(
              'latestCQLData.authorFirstAgent',
              ReportAggregationType.TERMS
            )
              .withDocCount()
              .build()
          )
          .build()
      )
      .withFilter('latestCQLData.score', ReportFilterOperation.HAS_PROPERTY)
      .withFilter('latestCQLData.score', ReportFilterOperation.GTE, 1)
      .withQueryName(metric),
    [RTDReportTypes.LEADS_SEARCH]: new QueryBuilder('latestCQLData.updatedAt')
      .withSize(1000)
      .withFilter('latestCQLData.score', ReportFilterOperation.HAS_PROPERTY)
      .withFilter('latestCQLData.score', ReportFilterOperation.GTE, 1)
      .withQueryName(metric),
    [RTDReportTypes.TOTAL_CONVOS]: new QueryBuilder()
      .withAggregation(
        new AggregationBuilder('firstAgentId', ReportAggregationType.TERMS)
          .withDocCount()
          .withMissingValue(botId ?? 0)
          .build()
      )
      .withQueryName(metric),
    [RTDReportTypes.MEETINGS_BOOKED]: new QueryBuilder('createdAt')
      .withAggregation(
        new AggregationBuilder('agentId', ReportAggregationType.TERMS)
          .withDocCount()
          .build()
      )
      .withFilter('meetingSource', ReportFilterOperation.IN, [
        'CALENDAR_DROP',
        'PLAYBOOK_DROP',
        'PLAYBOOK_DROP_TEAM',
      ])
      .withQueryName(metric),
  }
}

const PARSERS_BY_METRIC: Record<string, ParserFunction> = {
  [RTDReportTypes.LOW_CSAT]: Parsers.parseCSATByConversation,
  [RTDReportTypes.NOT_RESPONDED]: Parsers.parseAsKeyValueObject,
  [RTDReportTypes.REP_PERFORMANCE]: Parsers.parseRealTimeRepPerformanceTable,
  [RTDReportTypes.CHAT_RATINGS]: Parsers.parseConversationRatingsByRep,
  [RTDReportTypes.OPEN_CLOSED_CONVOS]: Parsers.parseOpenAndClosedCreatedByRep,
  [RTDReportTypes.MISSED_CHATS_AGENT]: Parsers.parseMissedChatsByAgent,
  [RTDReportTypes.MISSED_CHATS_TYPE]: Parsers.parseMissedChatsByType,
  [RTDReportTypes.LEADS]: Parsers.parseLeadsQualifiedByAgent,
  [RTDReportTypes.TOTAL_CONVOS]: Parsers.parseAsKeyValueObject,
  [RTDReportTypes.MEETINGS_BOOKED]: Parsers.parseAsKeyValueObject,
}

export const getQuery = (
  queryConfig: IRealTimeQueryConfig,
  options?: IQueryOptions
): {
  query: ReportQuery
  parseFunction: ParserFunction
} => {
  const { metric } = queryConfig
  const queriesByMetric = makeQueries({ ...queryConfig, ...options })

  const query = queriesByMetric[metric]

  return {
    query,
    parseFunction: PARSERS_BY_METRIC[metric],
  }
}

// TODO @cmcgorrill - we can't currently apply UI filters for missed chats exports
// once backend work is complete to support this, add this back in
//
// export const appendUIFiltersToQuery = (query: ReportQuery, metric, filterGroup) => {
// if(filterGroup.shouldMatch > 0){
//   //in our use case, there is only ever one filter applied -- team or teammate
//   const filter = filterGroup.filters[0]
//   if(filter.property === 'team'){

//   }
//   else if(filter.property === 'teammate'){
//     let newFilter = {
//       operation: filter.operation,
//       values: filter.values,
//       property: '',
//     }
//     switch(metric){
//       case 'missedChatsSearch':
//         newFilter.property = 'firstRoutingAction.participants[0]'
//         break
//       case 'leadsQualifiedSearch':
//         newFilter.property = 'latestCQLData.authorId'
//         break
//       default:
//         return query

//     }
//     const filterGroupToAdd = {
//       shouldMatch: filterGroup.shouldMatch,
//       negateFilterGroup: filterGroup.negateFilterGroup,
//       filters: [newFilter],
//     }
//     query.filterGroups?.push(filterGroupToAdd)
//   }
// }
// return query
// }
